import React, { useEffect, useState } from 'react'
import { deleteTask, moveTask, updateTask } from '../../flux/tasks/TaskActions'
import TaskStore, { EventsEnum } from '../../flux/tasks/TaskStore'
import EditTaskComponent from './EditTask'
import TaskComponent, { ITask } from './Task'

/**
 * Control task component properties
 */
export interface IControlTaskProps {
    boardId: number
    swimlaneId: number
    taskId: number
}

function ControlTaskComponent(props: IControlTaskProps) {
    const [task, setTask] = useState(
        TaskStore.getTask(props.boardId, props.swimlaneId, props.taskId)
    )
    const [isEdit, setIsEdit] = useState(false)

    /**
     * Handles onEdit from Task Component.
     * Sets isEdit state to true.
     */
    function handleEdit() {
        setIsEdit(true)
    }

    /**
     * Handles onCancel from EditTaskComponent.
     * Sets isEdit state to false
     */
    function handleCancel() {
        setIsEdit(false)
    }

    /**
     * Handles a save request from the EditTaskComponent
     */
    function handleSave(task: ITask) {
        setIsEdit(false)
        updateTask(props.boardId, props.swimlaneId, task)
    }

    /**
     * Handles the delete event
     */
    function handleDelete() {
        deleteTask(props.boardId, props.swimlaneId, task)
    }

    /**
     * Invoked when user starts dragging a task within the UI.
     * Sets data transfer properties in preperation for drop.
     * @param event Drag Event
     */
    function handleDragStart(event: React.DragEvent) {
        event.dataTransfer.setData('text/plain', JSON.stringify(props))
    }

    /**
     * Invoked when a task is dropped onto this task.
     * Should instigate the task move action
     * @param event Drag Event
     */
    function handleDrop(event: React.DragEvent) {
        event.stopPropagation()
        const from = JSON.parse(
            event.dataTransfer.getData('text/plain')
        ) as IControlTaskProps
        moveTask(
            from.boardId,
            from.swimlaneId,
            from.taskId,
            props.boardId,
            props.swimlaneId,
            props.taskId
        )
    }

    /**
     * Invoked as part of the droppable functionality.
     * prevent default to identify this element as a valid drop target.
     * (may add logic later to restrict this to task drops only)
     * @param event Drag Event
     */
    function handleDragEnter(event: React.DragEvent) {
        event.preventDefault()
    }

    /**
     * Invoked as part of the droppable functionality.
     * prevent default to identify this element as a valid drop target.
     * (may add logic later to restrict this to task drops only)
     * @param event Drag Event
     */
    function handleDragOver(event: React.DragEvent) {
        event.preventDefault()
    }

    useEffect(() => {
        /**
         * Handles the edit event from the task store.
         * If this task is the one which has been edited then update state
         * @param boardId Board ID
         * @param swimlaneId Swimlane ID
         * @param taskId Task ID
         */
        function handleTaskEdit(
            boardId: number,
            swimlaneId: number,
            taskId: number
        ) {
            if (
                !(
                    boardId === props.boardId &&
                    swimlaneId === props.swimlaneId &&
                    taskId === props.taskId
                )
            )
                return
            setTask(
                TaskStore.getTask(props.boardId, props.swimlaneId, props.taskId)
            )
        }

        TaskStore.addTaskStoreListener(EventsEnum.EDIT, handleTaskEdit)
        return () => {
            TaskStore.removeTaskStoreListener(EventsEnum.EDIT, handleTaskEdit)
        }
    }, [task, props.boardId, props.swimlaneId, props.taskId])

    return (
        <>
            {isEdit && (
                <EditTaskComponent
                    task={task}
                    onCancel={handleCancel}
                    onSave={handleSave}
                />
            )}
            {!isEdit && (
                <TaskComponent
                    task={task}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                />
            )}
        </>
    )
}

export default ControlTaskComponent
